import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "react-hot-toast";

import {
  flattenConversationPages,
  useChatConversations,
  useSessionUser,
} from "@web-app/api";
import { useAlert } from "@web-app/components";
import { useDocument } from "@web-app/hooks";
import { Route } from "@web-app/routes/chat.route";

import Navigation from "../common/Navigation";

import ChatContainer from "./ChatContainer";
import ConversationList from "./ConversationList";
import {
  getConversationDisplayImage,
  getConversationDisplayName,
} from "./chatUtils";

export default function ChatPage({ logout }: { logout: () => void }) {
  useDocument({ title: "Inbox" });

  const { conversationId: selectedConversationId } = Route.useSearch();
  const navigate = useNavigate();
  const { data: conversationsData } = useChatConversations();
  const conversations = flattenConversationPages(conversationsData);
  const { data: currentUser } = useSessionUser();
  const { showAlert } = useAlert();

  const selectedConversation = conversations.find(
    (conv) => conv.conversation.id === selectedConversationId,
  );

  const hasValidSelection = !!(selectedConversation && currentUser);

  useEffect(() => {
    if (selectedConversationId && !selectedConversation) {
      // If the conversationId in the URL doesn't match any conversation, clear it
      navigate({
        search: { conversationId: undefined },
        to: "/chat",
      });

      showAlert({
        message: "The selected conversation could not be found.",
        title: "Conversation Not Found",
        type: "error",
      });
    }
  }, [selectedConversationId, selectedConversation, navigate]);

  const handleSelectConversation = (conversationId: string) => {
    navigate({
      search: { conversationId },
      to: "/chat",
    });
  };

  const handleBack = () => {
    navigate({
      search: { conversationId: undefined },
      to: "/chat",
    });
  };

  return (
    <div>
      <Navigation logout={logout} />
      <Toaster position="top-center" />

      <div className="ml-0 lg:ml-20 bg-gray-100 h-[calc(100dvh-44px)] lg:h-screen flex">
        {/* Conversation List - hidden on mobile when a valid conversation is selected */}
        <div
          className={`w-full lg:w-[466px] flex-shrink-0 ${
            hasValidSelection ? "hidden lg:block" : "block"
          }`}
        >
          <ConversationList
            selectedConversationId={selectedConversationId}
            onSelectConversation={handleSelectConversation}
          />
        </div>

        {/* Chat Container - full width on mobile when a valid conversation is selected */}
        <div
          className={`flex-1 bg-white ${
            hasValidSelection ? "block" : "hidden lg:block"
          }`}
        >
          {selectedConversation && currentUser ? (
            <ChatContainer
              key={selectedConversation.conversation.id}
              conversationId={selectedConversation.conversation.id}
              conversationType={selectedConversation.conversation.type}
              driverImage={getConversationDisplayImage(
                selectedConversation,
                currentUser.id,
              )}
              driverName={getConversationDisplayName(
                selectedConversation,
                currentUser.id,
              )}
              onBack={handleBack}
              onClose={() =>
                navigate({
                  search: { conversationId: undefined },
                  to: "/chat",
                })
              }
              orderAppAvatarUrl={selectedConversation.orderAppAvatarUrl}
              orderDisplayId={selectedConversation.orderDisplayId}
              orderExternalId={selectedConversation.orderExternalId}
              participantsWithDetails={
                selectedConversation.participantsWithDetails
              }
              providerState={selectedConversation.conversation.providerState}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm mt-1">
                  Choose a conversation from the list to start chatting
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
